/**
 * Search Palette - Fuzzy search over plan content
 *
 * Sublime Text-style fuzzy matching with instant results.
 * Opens with Cmd/Ctrl+K.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { fuzzySearch, type FuzzyResult, type SearchableItem } from "../lib/fuzzySearch";

interface SearchPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  getSearchIndex: () => SearchableItem[];
  onSelectTask: (taskId: string) => void;
}

/**
 * Format a ref for display - shortens URLs to domain + path hint
 */
function formatRef(ref: string): string {
  if (ref.startsWith("http")) {
    try {
      const url = new URL(ref);
      const path = url.pathname.split("/").filter(Boolean);
      // Show domain + last path segment if available
      if (path.length > 0) {
        return `${url.hostname}/…/${path[path.length - 1]}`;
      }
      return url.hostname;
    } catch {
      return ref.slice(0, 30);
    }
  }
  return ref;
}

export default function SearchPalette({
  isOpen,
  onClose,
  getSearchIndex,
  onSelectTask,
}: SearchPaletteProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedIndex, _setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Wrapper to keep ref in sync with state
  const setSelectedIndex = useCallback((update: number | ((prev: number) => number)) => {
    _setSelectedIndex(prev => {
      const next = typeof update === "function" ? update(prev) : update;
      selectedIndexRef.current = next;
      return next;
    });
  }, []);

  // Debounce query - 50ms delay for batching rapid keystrokes
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 50);
    return () => clearTimeout(timer);
  }, [query]);

  // Search results - compute on debounced query for display
  const results = useMemo(() => {
    if (!debouncedQuery.trim()) return [];
    return fuzzySearch(debouncedQuery, getSearchIndex(), 20);
  }, [debouncedQuery, getSearchIndex]);

  // Keep a ref to results for keyboard handler (avoid stale closure)
  const resultsRef = useRef(results);
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);

  // Reset selection only when query changes (not when results reference changes)
  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedQuery, setSelectedIndex]);

  // Reset state and focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setDebouncedQuery("");
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, setSelectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && results.length > 0) {
      const selected = listRef.current.children[selectedIndex] as HTMLElement;
      selected?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, results.length]);

  const handleSelect = useCallback((result: FuzzyResult) => {
    // For task type, select directly
    if (result.item.type === "task") {
      onSelectTask(result.item.id);
    }
    onClose();
  }, [onSelectTask, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const currentResults = resultsRef.current;
    switch (e.key) {
      case "Escape":
        onClose();
        break;
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, currentResults.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (currentResults[selectedIndexRef.current]) {
          handleSelect(currentResults[selectedIndexRef.current]);
        }
        break;
    }
  }, [handleSelect, onClose, setSelectedIndex]);

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-4 sm:pt-[15vh] px-3 sm:px-0 transition-opacity duration-100 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-base-100 rounded-lg shadow-2xl w-full max-w-xl overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 border-b border-base-300">
          <svg className="w-5 h-5 text-base-content/40 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent outline-none text-base sm:text-lg min-w-0"
            placeholder="Search tasks..."
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="kbd kbd-sm text-base-content/40 hidden sm:inline-flex">⌘K</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] sm:max-h-80 overflow-auto">
          {query && results.length === 0 && (
            <div className="px-3 sm:px-4 py-8 text-center text-base-content/50">
              No matches for "{query}"
            </div>
          )}

          {results.map((result, index) => (
            <button
              key={result.item.id}
              type="button"
              className={`w-full px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3 text-left transition-colors ${
                index === selectedIndex
                  ? "bg-primary/10"
                  : "hover:bg-base-200 active:bg-base-200"
              }`}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {/* Type Icon */}
              <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium ${
                result.item.type === "task" ? "bg-primary/20 text-primary" :
                result.item.type === "rule" ? "bg-secondary/20 text-secondary" :
                result.item.type === "fact" ? "bg-accent/20 text-accent" :
                "bg-base-300 text-base-content/60"
              }`}>
                {result.item.type === "task" ? "T" :
                 result.item.type === "rule" ? "R" :
                 result.item.type === "fact" ? "F" : "A"}
              </span>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <HighlightedText
                    text={result.item.text}
                    matches={result.matches}
                  />
                  {result.item.status && (
                    <span className={`badge badge-xs ${
                      result.item.status === "done" ? "badge-success" :
                      result.item.status === "ready" ? "badge-primary" :
                      result.item.status === "in_progress" ? "badge-secondary" :
                      result.item.status === "blocked" ? "badge-warning" :
                      "badge-ghost"
                    }`}>
                      {result.item.status.replace("_", " ")}
                    </span>
                  )}
                </div>
                {result.item.secondary && (
                  <div className="text-sm text-base-content/50 truncate">
                    {result.item.secondary}
                  </div>
                )}
                {result.item.refs && result.item.refs.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap overflow-hidden max-h-6">
                    {result.item.refs.slice(0, 3).map((ref, i) => (
                      <span key={i} className="badge badge-xs badge-outline max-w-32 truncate" title={ref}>
                        {formatRef(ref)}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Score (debug) */}
              {/* <span className="text-xs text-base-content/30">{result.score.toFixed(1)}</span> */}
            </button>
          ))}
        </div>

        {/* Footer - keyboard hints hidden on mobile */}
        {!query && (
          <div className="hidden sm:block px-4 py-3 border-t border-base-300 text-sm text-base-content/50">
            <span className="mr-4"><kbd className="kbd kbd-xs">↑↓</kbd> navigate</span>
            <span className="mr-4"><kbd className="kbd kbd-xs">↵</kbd> select</span>
            <span><kbd className="kbd kbd-xs">esc</kbd> close</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Render text with highlighted match characters
 */
function HighlightedText({ text, matches }: { text: string; matches: number[] }) {
  if (matches.length === 0) {
    return <span className="font-mono">{text}</span>;
  }

  // Group consecutive matches to reduce number of elements
  const parts: React.ReactNode[] = [];
  const matchSet = new Set(matches);
  let i = 0;

  while (i < text.length) {
    if (matchSet.has(i)) {
      // Collect consecutive highlighted chars
      let end = i;
      while (end < text.length && matchSet.has(end)) end++;
      parts.push(
        <span key={i} className="bg-primary/30 text-primary">{text.slice(i, end)}</span>
      );
      i = end;
    } else {
      // Collect consecutive non-highlighted chars
      let end = i;
      while (end < text.length && !matchSet.has(end)) end++;
      parts.push(<span key={i}>{text.slice(i, end)}</span>);
      i = end;
    }
  }

  return <span className="font-mono">{parts}</span>;
}
